import * as React from "react";
import { useEffect, useState } from "react";
import { Box, Alert, Typography } from "@mui/material";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import { useAuth } from "../contexts/AuthContext";
import { axiosInstance } from "../services/apiClient";
import {
  ReadOnlyDataGrid,
  ReadOnlyField,
} from "../components/ReadOnlyDataGrid";

export interface InviteRecord {
  id: string;
  email: string;
  role: string;
  created_at: string;
  expires_at: string;
  accepted_at: string | null;
  status: string;
  referrer_user_id: string;
  referrer_email: string;
}

/** Backend sends naive UTC datetimes; parse as UTC and show in Italy (Europe/Rome). */
function parseServerDateAsUtc(isoLike: string): Date {
  const s = isoLike.trim();
  const hasExplicitTz =
    /Z$/i.test(s) || /[+-]\d{2}:\d{2}$/.test(s) || /[+-]\d{4}$/.test(s);
  if (hasExplicitTz) {
    return new Date(s);
  }
  if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(s)) {
    const normalized = s.includes("T") ? s : s.replace(/^(\d{4}-\d{2}-\d{2})\s+/, "$1T");
    return new Date(`${normalized}Z`);
  }
  return new Date(s);
}

function formatLocalizedDateTime(value: unknown): string {
  if (value == null || value === "") return "";
  const d = parseServerDateAsUtc(String(value));
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("it-IT", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Europe/Rome",
  }).format(d);
}

function formatInviteStatus(value: unknown): string {
  const s = String(value ?? "");
  const map: Record<string, string> = {
    Sended: "Inviato",
    Expired: "Scaduto",
    Accepted: "Accettato",
  };
  return map[s] ?? s;
}

function formatUserRole(value: unknown): string {
  const s = String(value ?? "");
  const map: Record<string, string> = {
    baseUser: "Utente base",
    mantainer: "Mantainer",
    admin: "Amministratore",
  };
  return map[s] ?? s;
}

export const InvitesPage: React.FC = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState<InviteRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const hasPermission = user?.role === "admin" || user?.role === "mantainer";

  useEffect(() => {
    if (!hasPermission) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } =
          await axiosInstance.get<InviteRecord[]>("/admin/invites");
        if (!cancelled) setRows(data);
      } catch (e: unknown) {
        if (!cancelled) {
          const msg =
            e &&
            typeof e === "object" &&
            "response" in e &&
            (e as { response?: { data?: { detail?: string } } }).response?.data
              ?.detail;
          setError(
            typeof msg === "string" ? msg : "Impossibile caricare gli inviti"
          );
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasPermission]);

  const inviteFields: ReadOnlyField[] = [
    {
      field: "email",
      headerName: "Email",
      width: 220,
    },
    {
      field: "role",
      headerName: "Ruolo",
      width: 140,
      valueFormatter: (v) => formatUserRole(v),
    },
    {
      field: "created_at",
      headerName: "Inviato il",
      width: 200,
      valueFormatter: (v) => formatLocalizedDateTime(v),
    },
    {
      field: "expires_at",
      headerName: "Scadenza",
      width: 200,
      valueFormatter: (v) => formatLocalizedDateTime(v),
    },
    {
      field: "accepted_at",
      headerName: "Accettato il",
      width: 200,
      valueFormatter: (v) => formatLocalizedDateTime(v),
    },
    {
      field: "status",
      headerName: "Stato",
      width: 130,
      valueFormatter: (v) => formatInviteStatus(v),
    },
    {
      field: "referrer_email",
      headerName: "Invitato da",
      width: 220,
    },
  ];

  if (!hasPermission) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Non hai i permessi per visualizzare gli inviti. Solo amministratori e
          Mantainer possono accedere a questa sezione.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <ContactMailIcon fontSize="large" />
          <Typography variant="h5">Inviti</Typography>
        </Box>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <ReadOnlyDataGrid
        fields={inviteFields}
        data={rows}
        loading={loading}
        height="calc(100vh - 110px)"
        getRowId={(row) => row.id}
        initialPageSize={50}
        pageSizeOptions={[25, 50, 100]}
        initialSortModel={[{ field: "created_at", sort: "desc" }]}
      />
    </Box>
  );
};
